import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, ShoppingBag, Trash2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import {
  createLatdiorOrder,
  createServiceOrder,
  createStudioBooking,
  getPricing,
} from "../lib/firestoreActions";
import { formatCurrency } from "../lib/platformData";
import { CHECKOUT_DRAFT_KEY, isStripeCheckoutEnabled, startCheckoutSession } from "../lib/stripeCheckout";
import { useToast } from "../context/ToastContext";

function itemTotal(item) {
  if (item.type === "studio") return Number(item.total || 0);
  return Number(item.unitPrice || 0) * Number(item.quantity || 1);
}

function itemMeta(item) {
  if (item.type === "studio") {
    const range = item.startTime && item.endTime ? `${item.startTime} - ${item.endTime}` : (item.hours || []).join(", ");
    return `${item.sessionLabel || item.name} - ${item.date} - ${range} - ${item.duration || item.hours?.length || 0}h`;
  }
  if (item.type === "service") {
    return `${(item.files || []).length} fichier(s) ajoute(s)`;
  }
  if (item.type === "latdior") {
    return item.size ? `Taille ${item.size}` : "Taille a confirmer";
  }
  return "";
}

export default function CartPage() {
  const { user } = useAuth();
  const { items, totals, updateQuantity, removeItem, clearCart } = useCart();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const navigate = useNavigate();
  const stripeEnabled = isStripeCheckoutEnabled();
  const toast = useToast();

  const grouped = useMemo(
    () => ({
      studio: items.filter((item) => item.type === "studio"),
      services: items.filter((item) => item.type === "service"),
      latdior: items.filter((item) => item.type === "latdior"),
    }),
    [items]
  );

  const checkout = async () => {
    setError("");
    setSuccess("");

    if (!user) {
      navigate("/connexion", { state: { from: "/panier" } });
      return;
    }

    if (!items.length) return;

    setBusy(true);
    try {
      const pricing = await getPricing();
      const fingerprint = JSON.stringify(items);
      let draft;
      try {
        draft = JSON.parse(window.sessionStorage.getItem(CHECKOUT_DRAFT_KEY) || "null");
      } catch {
        draft = null;
      }
      if (draft?.userId !== user.uid || draft?.fingerprint !== fingerprint) {
        draft = { userId: user.uid, fingerprint, orderRefs: [] };
      }
      const orderRefs = draft.orderRefs;
      const persistDraft = () => window.sessionStorage.setItem(CHECKOUT_DRAFT_KEY, JSON.stringify(draft));

      for (const studioItem of grouped.studio) {
        if (orderRefs.some((ref) => ref.itemKey === studioItem.cartId)) continue;
        const id = await createStudioBooking(user, studioItem, pricing);
        orderRefs.push({ type: "studioBooking", id, itemKey: studioItem.cartId });
        persistDraft();
      }

      for (const serviceItem of grouped.services) {
        if (orderRefs.some((ref) => ref.itemKey === serviceItem.cartId)) continue;
        const id = await createServiceOrder(user, serviceItem);
        orderRefs.push({ type: "serviceOrder", id, itemKey: serviceItem.cartId });
        persistDraft();
      }

      if (grouped.latdior.length && !orderRefs.some((ref) => ref.type === "latdiorOrder")) {
        const id = await createLatdiorOrder(user, grouped.latdior);
        orderRefs.push({ type: "latdiorOrder", id });
        persistDraft();
      }

      const paymentItems = [...grouped.services, ...grouped.latdior];
      if (paymentItems.length && stripeEnabled) {
        try {
          const redirected = await startCheckoutSession({
            orderRefs: orderRefs.filter((ref) => ref.type !== "studioBooking"),
          });
          if (redirected) {
            return;
          }
        } catch (stripeError) {
          setError("Le paiement n'a pas démarré. Tes commandes sont enregistrées ; tu peux réessayer sans les créer une seconde fois.");
          toast.error("Paiement indisponible", stripeError.message || "Réessaie dans un instant.");
          return;
        }
        setError("Le paiement n'a pas démarré. Réessaie dans un instant.");
        return;
      }

      clearCart();
      window.sessionStorage.removeItem(CHECKOUT_DRAFT_KEY);
      setSuccess("Demande enregistrée. Suis son statut dans ton compte.");
      toast.success("Demande enregistrée", "Retrouve son statut dans ton compte.");
    } catch (err) {
      setError(err.message || "Impossible de valider le panier.");
      toast.error("Validation impossible", err.message || "Réessaie dans un instant.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-screen bg-black px-6 py-24 text-white md:px-20">
      <section className="mx-auto max-w-6xl">
        <p className="mb-4 text-sm font-semibold uppercase tracking-[0.25em] text-red-500">
          Panier global
        </p>
        <div className="mb-10 flex flex-col justify-between gap-4 md:flex-row md:items-end">
          <div>
            <h1 className="text-5xl font-black uppercase leading-none md:text-7xl">
              Recapitulatif.
            </h1>
            <p className="readable-copy mt-4 max-w-2xl text-gray-400">
              Studio, services audio et Latdior sont regroupes ici avant validation.
            </p>
          </div>
          <Link to="/compte" className="inline-flex w-fit rounded-full border border-white/15 px-5 py-3 text-sm font-bold uppercase">
            Mon compte
          </Link>
        </div>

        {error && <p className="mb-4 rounded-lg border border-red-500/25 bg-red-950/40 p-3 text-sm text-red-100">{error}</p>}
        {success && <p className="mb-4 rounded-lg border border-white/10 bg-zinc-950 p-3 text-sm text-gray-200">{success}</p>}

        {!items.length ? (
          <div className="rounded-lg border border-white/10 bg-zinc-950 p-8 text-center">
            <ShoppingBag className="mx-auto mb-4 text-gray-500" size={36} />
            <h2 className="text-2xl font-black uppercase">Panier vide</h2>
            <p className="mt-3 text-gray-400">Ajoute une session, un service ou un produit Latdior.</p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Link to="/reservation-studio" className="rounded-full bg-white px-5 py-2 text-sm font-bold uppercase text-black">
                Reserver le studio
              </Link>
              <Link to="/services" className="rounded-full border border-white/15 px-5 py-2 text-sm font-bold uppercase">
                Services
              </Link>
              <Link to="/latdior" className="rounded-full border border-white/15 px-5 py-2 text-sm font-bold uppercase">
                Latdior
              </Link>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_360px]">
            <div className="space-y-4">
              {items.map((item) => (
                <article key={item.cartId} className="rounded-lg border border-white/10 bg-zinc-950 p-4">
                  <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <div className="min-w-0">
                      <p className="text-xs font-bold uppercase tracking-[0.2em] text-red-500">
                        {item.type === "studio" ? "Studio" : item.type === "service" ? "Service" : "Latdior"}
                      </p>
                      <h2 className="mt-2 text-2xl font-bold">{item.name}</h2>
                      <p className="mt-1 text-sm text-gray-400">{itemMeta(item)}</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                      {item.type !== "studio" && (
                        <div className="inline-flex items-center rounded-full border border-white/15">
                          <button
                            onClick={() => updateQuantity(item.cartId, Number(item.quantity || 1) - 1)}
                            className="p-2"
                            aria-label="Diminuer"
                          >
                            <Minus size={16} />
                          </button>
                          <span className="min-w-8 text-center text-sm font-bold">{item.quantity || 1}</span>
                          <button
                            onClick={() => updateQuantity(item.cartId, Number(item.quantity || 1) + 1)}
                            className="p-2"
                            aria-label="Augmenter"
                          >
                            <Plus size={16} />
                          </button>
                        </div>
                      )}
                      <p className="min-w-[96px] text-right font-bold">{formatCurrency(itemTotal(item))}</p>
                      <button
                        onClick={() => removeItem(item.cartId)}
                        className="rounded-full border border-white/15 p-2 text-gray-300 hover:border-red-500 hover:text-red-500"
                        aria-label="Supprimer"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            <aside className="h-fit rounded-lg border border-white/10 bg-zinc-950 p-5">
              <h2 className="text-2xl font-black uppercase">Total</h2>
              <div className="mt-5 space-y-3 text-sm text-gray-300">
                <div className="flex justify-between">
                  <span>Elements</span>
                  <span>{totals.count}</span>
                </div>
                <div className="flex justify-between border-t border-white/10 pt-3 text-lg font-black text-white">
                  <span>A payer</span>
                  <span>{formatCurrency(totals.total)}</span>
                </div>
              </div>
              {!user && (
                <p className="mt-4 rounded border border-yellow-500/30 bg-yellow-500/10 p-3 text-sm text-yellow-100">
                  Connexion obligatoire avant validation.
                </p>
              )}
              <button
                onClick={checkout}
                disabled={busy}
                className="mt-5 w-full rounded-full bg-white px-5 py-3 text-sm font-bold uppercase text-black transition hover:bg-red-600 hover:text-white disabled:opacity-60"
              >
                {busy ? "Validation..." : user ? (stripeEnabled ? "Valider / payer" : "Valider la demande") : "Se connecter"}
              </button>
              <p className="readable-copy mt-4 text-xs text-gray-500">
                Le studio est soumis à validation. {stripeEnabled ? "Les services et vêtements sont réglés via Stripe." : "Le paiement en ligne n'est pas encore disponible pour les services et vêtements."}
              </p>
            </aside>
          </div>
        )}
      </section>
    </main>
  );
}
